import React, { useState, useEffect } from 'react';
import { TextField, TextFieldProps } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  dateField: {
    '& input': {
      fontFamily: 'monospace',
    },
  },
}));

interface DateFieldProps extends Omit<TextFieldProps, 'onChange'> {
  value: Date | null;
  onChange: (date: Date | null) => void;
}

/**
 * Custom date field component that handles date input in MM/DD/YYYY format
 */
const DateField: React.FC<DateFieldProps> = ({ value, onChange, ...props }) => {
  const classes = useStyles();
  const [inputValue, setInputValue] = useState<string>('');

  // Format date to MM/DD/YYYY
  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year = d.getFullYear();
      
      return `${month}/${day}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Parse date string to Date object
  const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    
    // Check if the string matches MM/DD/YYYY format
    const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = dateString.match(regex);
    
    if (!match) return null;
    
    const month = parseInt(match[1], 10) - 1; // Months are 0-indexed in JS
    const day = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    
    const date = new Date(year, month, day);
    
    // Validate the date is valid
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month ||
      date.getDate() !== day
    ) {
      return null;
    }
    
    return date;
  };

  // Format the date when the value prop changes
  useEffect(() => {
    setInputValue(formatDate(value));
  }, [value]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Auto-format as user types
    if (newValue.length === 2 && !newValue.includes('/') && inputValue.length === 1) {
      setInputValue(`${newValue}/`);
    } else if (newValue.length === 5 && newValue.charAt(2) === '/' && !newValue.includes('/', 3) && inputValue.length === 4) {
      setInputValue(`${newValue}/`);
    }
    
    // Only parse and call onChange when we have a complete date or empty string
    if (newValue === '' || (newValue.length >= 10 && newValue.includes('/'))) {
      const parsedDate = parseDate(newValue);
      onChange(parsedDate);
    }
  };

  // Handle blur to format the date properly when focus is lost
  const handleBlur = () => {
    const parsedDate = parseDate(inputValue);
    if (parsedDate) {
      setInputValue(formatDate(parsedDate));
      onChange(parsedDate);
    } else if (inputValue && inputValue.trim() !== '') {
      // If invalid date but not empty, reset to the original value
      setInputValue(formatDate(value));
    } else {
      // If empty, set to null
      onChange(null);
    }
  };

  return (
    <TextField
      {...props}
      className={`${classes.dateField} ${props.className || ''}`}
      value={inputValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      placeholder="MM/DD/YYYY"
      inputProps={{
        ...props.inputProps,
        maxLength: 10,
      }}
    />
  );
};

export default DateField;
